import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiFilter, FiEdit2, FiTrash2, FiEye, FiActivity, FiMoreVertical, FiCopy } from 'react-icons/fi';
import { exams as examsApi } from '../../api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-600',
  published: 'bg-emerald-50 text-emerald-700',
  active: 'bg-blue-50 text-blue-700',
  completed: 'bg-purple-50 text-purple-700',
  archived: 'bg-orange-50 text-orange-700',
};

export default function ExamListPage() {
  const [exams, setExams] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [openMenu, setOpenMenu] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await examsApi.getAll({ page, limit: 15, search, status });
      setExams(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch { toast.error('Failed to load exams'); }
    finally { setLoading(false); }
  }, [page, search, status]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await examsApi.delete(id);
      toast.success('Exam deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  const handlePublish = async (id, currentStatus) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    try {
      await examsApi.publish(id, newStatus);
      toast.success(`Exam ${newStatus === 'published' ? 'published' : 'unpublished'}`);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    setOpenMenu(null);
  };

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Exams</h1>
          <p className="text-gray-500 text-sm">{pagination.total || 0} exams total</p>
        </div>
        <Link to="/admin/exams/create" className="btn-primary self-start sm:self-auto">
          <FiPlus size={16} /> Create Exam
        </Link>
      </div>

      {/* Filters */}
      <div className="card p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input className="input pl-9" placeholder="Search exams..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <div className="flex items-center gap-2">
          <FiFilter size={15} className="text-gray-400" />
          <select className="input w-auto" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            {['draft','published','active','completed','archived'].map(s => (
              <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="table-th">Exam</th>
                <th className="table-th">Status</th>
                <th className="table-th hidden md:table-cell">Questions</th>
                <th className="table-th hidden md:table-cell">Duration</th>
                <th className="table-th hidden lg:table-cell">Candidates</th>
                <th className="table-th hidden lg:table-cell">Created</th>
                <th className="table-th text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(7)].map((_, j) => (
                      <td key={j} className="table-td"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              ) : exams.length === 0 ? (
                <tr><td colSpan={7} className="py-16 text-center">
                  <p className="text-gray-400 font-medium">No exams found</p>
                  <Link to="/admin/exams/create" className="text-navy text-sm font-semibold hover:underline mt-1 inline-block">Create one →</Link>
                </td></tr>
              ) : exams.map(exam => (
                <tr key={exam.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="table-td">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-navy text-xs font-bold">{exam.title?.[0]?.toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 max-w-[200px] truncate">{exam.title}</p>
                        {exam.category && <p className="text-xs text-gray-400">{exam.category}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="table-td">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full capitalize ${STATUS_STYLES[exam.status] || 'bg-gray-100 text-gray-600'}`}>
                      {exam.status}
                    </span>
                  </td>
                  <td className="table-td hidden md:table-cell">
                    <span className="font-medium">{exam.question_count || 0}</span>
                    <span className="text-gray-400"> / {exam.total_questions}</span>
                  </td>
                  <td className="table-td hidden md:table-cell">{exam.duration_minutes} min</td>
                  <td className="table-td hidden lg:table-cell">
                    <span className="text-gray-700">{exam.registered_count || 0}</span>
                    {exam.active_count > 0 && <span className="ml-2 text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">{exam.active_count} live</span>}
                  </td>
                  <td className="table-td hidden lg:table-cell text-gray-400 text-xs">
                    {formatDistanceToNow(new Date(exam.created_at), { addSuffix: true })}
                  </td>
                  <td className="table-td text-right">
                    <div className="flex items-center justify-end gap-1 relative">
                      <Link to={`/admin/exams/${exam.id}`} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-navy" title="View">
                        <FiEye size={15} />
                      </Link>
                      <Link to={`/admin/exams/${exam.id}`} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-navy" title="Edit">
                        <FiEdit2 size={15} />
                      </Link>
                      {exam.status === 'in_progress' && (
                        <Link to={`/admin/exams/${exam.id}/monitor`} className="p-1.5 rounded hover:bg-blue-50 text-blue-500" title="Monitor">
                          <FiActivity size={15} />
                        </Link>
                      )}
                      <div className="relative">
                        <button onClick={() => setOpenMenu(openMenu === exam.id ? null : exam.id)}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
                          <FiMoreVertical size={15} />
                        </button>
                        {openMenu === exam.id && (
                          <div className="absolute right-0 top-8 w-44 bg-white border border-gray-100 rounded-xl shadow-lg z-10 py-1">
                            <button onClick={() => handlePublish(exam.id, exam.status)}
                              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">
                              {exam.status === 'published' ? 'Unpublish' : 'Publish'}
                            </button>
                            <Link to={`/admin/exams/${exam.id}/monitor`}
                              className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50">
                              <FiActivity size={13} /> Live Monitor
                            </Link>
                            <hr className="my-1 border-gray-100" />
                            <button onClick={() => { handleDelete(exam.id, exam.title); setOpenMenu(null); }}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                              <FiTrash2 size={13} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            <p className="text-sm text-gray-500">
              Showing {((page-1)*15)+1}–{Math.min(page*15, pagination.total)} of {pagination.total}
            </p>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p-1)}
                className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 hover:bg-white transition-colors">← Prev</button>
              <button disabled={page >= pagination.totalPages} onClick={() => setPage(p => p+1)}
                className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 hover:bg-white transition-colors">Next →</button>
            </div>
          </div>
        )}
      </div>

      {/* Close menu on outside click */}
      {openMenu && <div className="fixed inset-0 z-0" onClick={() => setOpenMenu(null)} />}
    </div>
  );
}
